import type { DigioSdkCallbackResponse, DigioSdkOptions } from "../types/digio";

const DIGIO_SDK_URLS = {
  sandbox: "https://ext.digio.in/sdk/v11/digio.js",
  production: "https://app.digio.in/sdk/v11/digio.js",
} as const;

type DigioEnvironment = keyof typeof DIGIO_SDK_URLS;

let sdkLoadPromise: Promise<void> | null = null;

export const getDigioEnvironment = (): DigioEnvironment => {
  const env = (process.env.REACT_APP_DIGIO_ENVIRONMENT || "sandbox").toLowerCase();
  return env === "production" ? "production" : "sandbox";
};

const loadDigioScript = async (environment: DigioEnvironment = getDigioEnvironment()) => {
  if (typeof window === "undefined") {
    throw new Error("Digio SDK can only be loaded in the browser");
  }

  if (window.Digio) {
    return;
  }

  if (!sdkLoadPromise) {
    sdkLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = DIGIO_SDK_URLS[environment];
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Digio SDK"));
      document.body.appendChild(script);
    });
  }

  await sdkLoadPromise;
};

export interface StartDigioSignParams {
  documentId: string;
  identifier: string;
  accessToken?: string;
  onSuccess?: (response: DigioSdkCallbackResponse) => void;
  onError?: (response: DigioSdkCallbackResponse) => void;
  onEvent?: (event: Record<string, unknown>) => void;
}

export const startDigioSign = async ({
  documentId,
  identifier,
  accessToken,
  onSuccess,
  onError,
  onEvent,
}: StartDigioSignParams) => {
  const environment = getDigioEnvironment();
  await loadDigioScript(environment);

  if (!window.Digio) {
    throw new Error("Digio SDK is not available");
  }

  const options: DigioSdkOptions = {
    environment,
    is_iframe: true,
    logo: `${window.location.origin}/MHFavicon.svg`,
    theme: {
      primaryColor: "#2F70F2",
      secondaryColor: "#282A30",
    },
    event_listener: (event) => {
      onEvent?.(event);
    },
    callback: (response) => {
      if (response?.error_code) {
        onError?.(response);
        return;
      }
      onSuccess?.(response);
    },
  };

  const digio = new window.Digio(options);
  digio.init();

  if (accessToken) {
    digio.submit(documentId, identifier, accessToken);
    return;
  }

  digio.submit(documentId, identifier);
};
